import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Container, Typography, Grid, Card, CardContent,
  CardActions, Chip, IconButton, useMediaQuery, Drawer, List,
  ListItem, ListItemText, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  CheckCircle, TrendingUp, ShowChart, Menu as MenuIcon, Close as CloseIcon,
  ArrowForward, VerifiedUser, Speed, Psychology, AutoGraph, Devices,
  AccountTree, Insights, Shield, Bolt, CandlestickChart, Hub,
  QueryStats, Webhook, SmartToy, ExpandMore, RocketLaunch,
  Timeline, Security, MonetizationOn, Layers, DataObject,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import WorldMapCanvas from './WorldMapCanvas';
import ChartHeroBg from './ChartHeroBg';
import NeuralHeroBg from './NeuralHeroBg';
import CodeHeroBg from './CodeHeroBg';
import InfraHeroBg from './InfraHeroBg';
import SpeedHeroBg from './SpeedHeroBg';
import MarketSessionsMonitor from '../components/widgets/MarketSessionsMonitor';
import CryptoTicker from '../components/CryptoTicker';

// ─── DARK THEME ──────────────────────────────────────────────────────────────
const landingTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#00FF94' },
    background: { default: '#0A0E17', paper: '#111827' },
    text: { primary: '#F9FAFB', secondary: '#9CA3AF' },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.03em', fontFamily: '"Space Grotesk", "Inter", sans-serif', textShadow: '0 1px 0 rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.15)' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em', fontFamily: '"Space Grotesk", "Inter", sans-serif', textShadow: '0 1px 0 rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.15)' },
    h3: { fontWeight: 700, letterSpacing: '-0.01em', fontFamily: '"Space Grotesk", "Inter", sans-serif', textShadow: '0 1px 0 rgba(0,0,0,0.25), 0 2px 4px rgba(0,0,0,0.15)' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 12 },
        containedPrimary: {
          background: 'linear-gradient(135deg, #00FF94 0%, #00D67A 100%)',
          color: '#0A0E17',
          '&:hover': { background: 'linear-gradient(135deg, #33FFaa 0%, #00FF94 100%)', transform: 'translateY(-1px)', boxShadow: '0 8px 30px rgba(0,255,148,0.3)' },
        },
      },
    },
  },
});

// ─── SHINY TEXT HELPERS (metallic light sweep) ─────────────────────────────
const shinyText = (color) => ({
  display: 'inline-block',
  background: `linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%), linear-gradient(135deg, ${color}, ${color})`,
  backgroundSize: '300% 100%, 100% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: 'metalReflection 4s ease-in-out infinite',
});
const shinyGradient = (gradient) => ({
  display: 'inline-block',
  background: `linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%), ${gradient}`,
  backgroundSize: '300% 100%, 100% 100%',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: 'metalReflection 4s ease-in-out infinite',
});

// ─── INTERSECTION OBSERVER HOOK ──────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ─── CODE SCROLL REVEAL (line-by-line) ──────────────────────────────────────
const CodeScrollReveal = ({ children }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const lines = el.querySelectorAll('.code-line-scroll');
        lines.forEach((line, i) => {
          setTimeout(() => line.classList.add('visible'), i * 80);
        });
        obs.unobserve(el);
      }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <Box ref={ref}>{children}</Box>;
};

// ─── LIVE TICKING PORTFOLIO VALUE ────────────────────────────────────────────
const LivePortfolioValue = () => {
  const [value, setValue] = useState(164621.42);
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    const interval = setInterval(() => {
      const delta = (Math.random() - 0.4) * 50;
      setValue(prev => prev + delta);
      setFlash(delta >= 0 ? '#00FF94' : '#EF4444');
      setTimeout(() => setFlash(null), 600);
    }, 2500);
    return () => clearInterval(interval);
  }, []);
  return (
    <Typography sx={{
      fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 800,
      color: flash || '#F9FAFB',
      fontFamily: '"Space Grotesk", sans-serif', lineHeight: 1,
      transition: 'color 0.3s ease',
    }}>
      ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </Typography>
  );
};

const parseAnimatedMetric = (raw) => {
  const str = String(raw);
  const match = str.match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const numericText = match[0];
  const index = str.indexOf(numericText);
  return {
    value: parseFloat(numericText),
    prefix: str.slice(0, index),
    suffix: str.slice(index + numericText.length),
    decimals: (numericText.split('.')[1] || '').length,
    grouping: str.includes(',') || Math.abs(parseFloat(numericText)) >= 1000,
  };
};

// ─── COUNT-UP ANIMATION ─────────────────────────────────────────────────────
const CountUp = ({ value, prefix = '', suffix = '', duration = 1800, decimals = 0, grouping = false }) => {
  const [ref, inView] = useInView(0.3);
  const rawValue = typeof value === 'number' ? value : parseFloat(value.toString().replace(/[^0-9.\-]/g, ''));
  const target = Number.isFinite(rawValue) ? Math.abs(rawValue) : 0;
  const formatValue = (num) => (
    grouping
      ? num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      : num.toFixed(decimals)
  );
  const [display, setDisplay] = useState(prefix + formatValue(0) + suffix);
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (!inView || hasAnimated.current) return;
    hasAnimated.current = true;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setDisplay(prefix + formatValue(target * ease) + suffix);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target, prefix, suffix, duration, decimals, grouping]);
  return <span ref={ref}>{display}</span>;
};

// ─── ANIMATED PROGRESS BAR ──────────────────────────────────────────────────
const AnimatedBar = ({ width, gradient, delay = 0 }) => {
  const [ref, inView] = useInView(0.3);
  return (
    <Box ref={ref} sx={{ height: 8, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: inView ? width : '0%', background: gradient, borderRadius: 2, transition: `width 1.5s cubic-bezier(0.16,1,0.3,1) ${delay}s` }} />
    </Box>
  );
};

// ─── ANIMATED SECTION WRAPPER ────────────────────────────────────────────────
const FadeSection = ({ children, delay = 0, ...props }) => {
  const [ref, inView] = useInView();
  return (
    <Box ref={ref} sx={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(40px)', transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`, ...props.sx }} {...props}>
      {children}
    </Box>
  );
};

const ANIMATED_BG_MASK = 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 12%, rgba(0,0,0,1) 88%, transparent 100%)';

const useScrollFadeOpacity = () => {
  const ref = useRef(null);
  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    let rafId = null;
    let lastOpacity = -1;

    const updateOpacity = () => {
      rafId = null;
      const el = ref.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight || 1;
      const visiblePx = Math.max(0, Math.min(rect.bottom, viewportH) - Math.max(rect.top, 0));
      const visibleRatio = Math.min(1, visiblePx / Math.max(1, Math.min(rect.height, viewportH)));
      const centerY = rect.top + rect.height / 2;
      const centerDistance = Math.abs(centerY - viewportH / 2);
      const centerRatio = 1 - Math.min(1, centerDistance / ((viewportH + rect.height) * 0.5));
      const nextOpacity = Math.max(0, Math.min(1, visibleRatio * 0.75 + centerRatio * 0.25));

      if (Math.abs(nextOpacity - lastOpacity) > 0.02) {
        lastOpacity = nextOpacity;
        setOpacity(nextOpacity);
      }
    };

    const requestUpdate = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(updateOpacity);
    };

    requestUpdate();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return [ref, opacity];
};

const AnimatedBackgroundLayer = ({ children, sx = {} }) => {
  const [ref, opacity] = useScrollFadeOpacity();

  return (
    <Box
      ref={ref}
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity,
        transition: 'opacity 180ms linear',
        WebkitMaskImage: ANIMATED_BG_MASK,
        maskImage: ANIMATED_BG_MASK,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

// ─── GLOWING ORB BACKGROUND ─────────────────────────────────────────────────
const GlowOrb = ({ top, left, size = 400, color = 'rgba(0,255,148,0.07)' }) => (
  <Box sx={{ position: 'absolute', top, left, width: size, height: size, background: `radial-gradient(circle, ${color} 0%, transparent 70%)`, borderRadius: '50%', filter: 'blur(80px)', pointerEvents: 'none', zIndex: 0 }} />
);

// ─── GLASS CARD ─────────────────────────────────────────────────────────────
const GlassCard = ({ children, sx = {}, hover = true, ...props }) => (
  <Card elevation={0} sx={{
    background: 'rgba(17,24,39,0.6)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.16,1,0.3,1)',
    position: 'relative',
    '@keyframes cardShine': { '0%': { backgroundPosition: '200% center' }, '100%': { backgroundPosition: '-50% center' } },
    // Top glow line
    '&::before': {
      content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(99,102,241,0.5), transparent)', opacity: 0, transition: 'opacity 0.3s ease',
    },
    // Shine sweep
    '&::after': {
      content: '""', position: 'absolute', inset: 0, borderRadius: '16px', pointerEvents: 'none', opacity: 0,
      background: 'linear-gradient(105deg, transparent 0%, transparent 40%, rgba(255,255,255,0.03) 42%, rgba(255,255,255,0.08) 45%, rgba(255,255,255,0.12) 47%, rgba(255,255,255,0.08) 49%, rgba(255,255,255,0.03) 52%, transparent 54%, transparent 100%)',
      backgroundSize: '250% 100%', backgroundPosition: '200% center', transition: 'opacity 0.3s ease',
    },
    ...(hover && {
      '&:hover': { border: '1px solid rgba(0,255,148,0.2)', transform: 'translateY(-6px)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
      '&:hover::before': { opacity: 1 },
      '&:hover::after': { opacity: 1, animation: 'cardShine 1s ease-in-out forwards' },
    }),
    ...sx,
  }} {...props}>{children}</Card>
);

const PerformanceChartCard = () => {
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => setAnimateIn(true));
    return () => cancelAnimationFrame(rafId);
  }, []);

  const aiStroke = '#6F6CFF';
  const benchStroke = '#7C8DAE';
  const transition = 'all 1.7s cubic-bezier(0.16, 1, 0.3, 1)';
  const aiLinePath = 'M0,134 L48,120 L100,106 L150,94 L198,74 L248,60 L302,42 L342,28 L400,16';
  const aiAreaPath = `${aiLinePath} L400,160 L0,160 Z`;
  const benchLinePath = 'M0,114 L64,106 L128,98 L192,94 L256,86 L320,78 L400,72';
  const benchAreaPath = `${benchLinePath} L400,160 L0,160 Z`;

  return (
    <GlassCard
      hover={false}
      sx={{
        p: { xs: 2.5, md: 4 },
        background: 'rgba(30,41,59,0.72)',
        border: '1px solid rgba(71,85,105,0.72)',
        borderRadius: '28px',
        boxShadow: '0 24px 72px rgba(0,0,0,0.22)',
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: '1.45rem', md: '1.9rem' },
          fontWeight: 800,
          mb: { xs: 2.5, md: 3.5 },
          fontFamily: '"Space Grotesk", sans-serif',
        }}
      >
        Equity Curve vs Benchmark
      </Typography>

      <Box sx={{ mb: 3 }}>
        <svg viewBox="0 0 400 160" width="100%" height="260" preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
          <defs>
            <linearGradient id="perfAiFillSource" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={aiStroke} stopOpacity="0.32" />
              <stop offset="100%" stopColor={aiStroke} stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="perfBenchFillSource" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={benchStroke} stopOpacity="0.2" />
              <stop offset="100%" stopColor={benchStroke} stopOpacity="0.02" />
            </linearGradient>
            <filter id="perfAiGlowSource" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <path
            d={benchAreaPath}
            fill="url(#perfBenchFillSource)"
            style={{
              opacity: animateIn ? 1 : 0,
              transform: animateIn ? 'translateY(0px)' : 'translateY(10px)',
              transformOrigin: 'center bottom',
              transition,
              transitionDelay: '120ms',
            }}
          />
          <path
            d={aiAreaPath}
            fill="url(#perfAiFillSource)"
            style={{
              opacity: animateIn ? 1 : 0,
              transform: animateIn ? 'translateY(0px)' : 'translateY(16px)',
              transformOrigin: 'center bottom',
              transition,
              transitionDelay: '240ms',
            }}
          />

          <path
            d={benchLinePath}
            fill="none"
            stroke={benchStroke}
            strokeWidth="2.4"
            strokeDasharray="7 5"
            strokeLinecap="round"
            pathLength="100"
            style={{
              strokeDasharray: 100,
              strokeDashoffset: animateIn ? 0 : 100,
              opacity: animateIn ? 0.92 : 0.35,
              transition,
              transitionDelay: '120ms',
            }}
          />
          <path
            d={aiLinePath}
            fill="none"
            stroke={aiStroke}
            strokeWidth="3"
            strokeLinecap="round"
            filter="url(#perfAiGlowSource)"
            pathLength="100"
            style={{
              strokeDasharray: 100,
              strokeDashoffset: animateIn ? 0 : 100,
              opacity: animateIn ? 1 : 0.35,
              transition,
              transitionDelay: '240ms',
            }}
          />

          <circle
            cx="400"
            cy="16"
            r="5"
            fill={aiStroke}
            style={{
              opacity: animateIn ? 1 : 0,
              transform: animateIn ? 'scale(1)' : 'scale(0.3)',
              transformOrigin: '400px 16px',
              transition: `${transition}, transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)`,
              transitionDelay: '520ms',
            }}
          >
            <animate attributeName="r" values="5;7;5" dur="2.2s" repeatCount="indefinite" begin="1.4s" />
            <animate attributeName="opacity" values="1;0.55;1" dur="2.2s" repeatCount="indefinite" begin="1.4s" />
          </circle>
        </svg>
      </Box>

      <Box sx={{ display: 'flex', gap: { xs: 3, md: 5 }, mb: 3.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 3, borderRadius: 999, bgcolor: aiStroke, boxShadow: `0 0 20px ${aiStroke}55` }} />
          <Typography sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, color: '#D1D5DB' }}>Strategy Curve</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 3, borderRadius: 999, backgroundImage: `repeating-linear-gradient(90deg, ${benchStroke} 0, ${benchStroke} 10px, transparent 10px, transparent 16px)` }} />
          <Typography sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, color: '#9CA3AF' }}>Benchmark</Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <Box sx={{ bgcolor: 'rgba(17,24,39,0.3)', borderRadius: 3, p: { xs: 2.25, md: 2.5 } }}>
          <Typography sx={{ fontSize: '0.9rem', color: '#9CA3AF', mb: 0.7 }}>Indicators</Typography>
          <Typography sx={{ fontSize: { xs: '1.9rem', md: '2.1rem' }, fontWeight: 800, color: '#34D399', lineHeight: 1 }}>
            <CountUp value={13} duration={2000} />
          </Typography>
        </Box>
        <Box sx={{ bgcolor: 'rgba(17,24,39,0.3)', borderRadius: 3, p: { xs: 2.25, md: 2.5 } }}>
          <Typography sx={{ fontSize: '0.9rem', color: '#9CA3AF', mb: 0.7 }}>Timeframes</Typography>
          <Typography sx={{ fontSize: { xs: '1.9rem', md: '2.1rem' }, fontWeight: 800, color: '#7C83FF', lineHeight: 1 }}>
            <CountUp value={6} duration={2000} />
          </Typography>
        </Box>
      </Box>
    </GlassCard>
  );
};

// ─── FEATURES DATA ──────────────────────────────────────────────────────────
const features = [
  { icon: <AccountTree />, title: 'Visual Strategy Builder', desc: 'Drag-and-drop node-based strategy creation. Connect signals, filters, and execution nodes visually.' },
  { icon: <Insights />, title: 'Signal Matrix Engine', desc: '13 technical indicators (RSI, MACD, Bollinger, SuperTrend, Ichimoku, etc.) with weighted scoring and consensus modes.' },
  { icon: <Psychology />, title: 'AI-Powered Signals', desc: 'Multi-timeframe confluence analysis across 6 timeframes with adaptive confidence scoring.' },
  { icon: <Shield />, title: 'Rescue Scale-In System', desc: 'Intelligent cost-averaging with a configurable multi-level ladder and budget-aware execution controls.' },
  { icon: <Bolt />, title: 'Reactive Trailing Engine', desc: 'Sub-second trailing stops via Durable Objects. Zero-lock profit floors, velocity crash kill-switch.' },
  { icon: <CandlestickChart />, title: 'Advanced Profit Protection', desc: 'TP1 auto-close, profit-lock ratchet, trailing benefit, and absolute safety caps — all leverage-aware.' },
  { icon: <QueryStats />, title: 'Live P&L Dashboard', desc: 'Real-time trade history with gross/net P&L, fee tracking, win rate, monthly returns, and Lottie animations.' },
  { icon: <Hub />, title: 'Multi-Exchange Support', desc: 'KuCoin integration with spot, margin, and futures. Paper trading mode with realistic fee simulation.' },
  { icon: <Webhook />, title: 'TradingView Webhooks', desc: 'Connect TradingView alerts directly to your strategies for automated signal-to-trade execution.' },
  { icon: <SmartToy />, title: 'Experience Mode (AI Learning)', desc: 'AI learns from your trade outcomes. Snapshot-based performance analysis drives strategy improvements.' },
  { icon: <AutoGraph />, title: 'Technical Analysis Toolkit', desc: 'Built around 13 signal modules including RSI, MACD, Bollinger Bands, SuperTrend, Ichimoku, VWAP, ATR filters, and trend regime analysis.' },
  { icon: <Layers />, title: 'Strategy Presets', desc: 'Pre-configured modes: Fast Green Scalp, Trend Continuation, Range Scalper, Breakout Expansion, and more.' },
];

// ─── HOW IT WORKS ───────────────────────────────────────────────────────────
const steps = [
  { num: '01', title: 'Build Your Strategy', desc: 'Use the visual drag-and-drop builder to create your trading strategy. Choose from 13 indicators and multiple pre-configured presets.' },
  { num: '02', title: 'Configure Risk Controls', desc: 'Set stop-loss, take-profit, rescue rules, trailing behavior, and position sizing with leverage-aware controls.' },
  { num: '03', title: 'Deploy & Monitor', desc: 'Activate your strategy with one click. The AI scans markets every minute and manages positions with sub-second precision.' },
  { num: '04', title: 'Optimize & Learn', desc: 'Review P&L analytics, win rates, and AI experience snapshots. Refine your approach with data-driven insights.' },
];

// ─── PRICING ────────────────────────────────────────────────────────────────
const billingOptions = [
  { key: 'monthly', label: 'Monthly', suffix: '/mo', discount: null },
  { key: '6month', label: '6 Months', suffix: '/mo', discount: 'Save 10%' },
  { key: 'yearly', label: 'Yearly', suffix: '/mo', discount: 'Save 20%' },
];
const plans = [
  {
    name: 'Starter', prices: { monthly: 29, '6month': 26, yearly: 23 }, badge: null,
    desc: 'Perfect for exploring AI-powered trading',
    features: ['3 Active Strategies', 'Signal Matrix (5 indicators)', 'Paper Trading Mode', 'Basic P&L Dashboard', '1-Minute Monitoring', 'Email Support'],
    cta: 'Start Free Trial', highlighted: false,
  },
  {
    name: 'Professional', prices: { monthly: 79, '6month': 71, yearly: 63 }, badge: 'Most Popular',
    desc: 'For serious traders who demand an edge',
    features: ['15 Active Strategies', 'Full Signal Matrix (13 indicators)', 'Paper + Live Trading', 'Rescue Scale-In System', 'Reactive Trailing Engine', 'TradingView Webhooks', 'Experience Mode (AI Learning)', 'Strategy Presets Library', 'Priority Support'],
    cta: 'Get Started', highlighted: true,
  },
  {
    name: 'Enterprise', prices: { monthly: 199, '6month': 179, yearly: 159 }, badge: null,
    desc: 'Custom plans for teams that need higher limits and integration support',
    features: ['Unlimited Strategies', 'Everything in Professional', 'API Access & Custom Integrations', 'Dedicated Account Manager', 'Priority Support'],
    cta: 'Contact Sales', highlighted: false,
  },
];

// ─── TESTIMONIALS (removed — placeholder for verified user feedback) ────────

// ─── FAQ ────────────────────────────────────────────────────────────────────
const faqs = [
  { q: 'How does the AI trading system work?', a: 'Our platform uses multi-timeframe confluence analysis across 6 timeframes, combined with a Signal Matrix of 13 technical indicators. The AI generates trading signals with confidence scores, and the execution engine manages entries, exits, trailing stops, and rescue operations automatically.' },
  { q: 'Is my capital safe with automated trading?', a: 'Absolutely. Every position is protected by multiple safety layers: hard loss caps, absolute safety caps, velocity crash kill-switches, and leverage-aware stop-losses. The rescue scale-in system can also recover losing positions. Paper trading mode lets you test everything risk-free.' },
  { q: 'What exchanges are supported?', a: 'Currently KuCoin (spot, margin, and futures). We are adding Binance, Bybit, and OKX soon. Paper trading mode simulates all exchange features with realistic fee calculation.' },
  { q: 'Can I connect TradingView signals?', a: 'Yes! Our TradingView webhook integration lets you route TradingView alerts directly into your strategies for automated execution.' },
  { q: 'Do I need coding skills?', a: 'Not at all. The visual strategy builder uses drag-and-drop nodes. Pre-configured strategy presets (Scalp, Trend, Range, Breakout) get you started in minutes.' },
  { q: 'Can I cancel anytime?', a: 'Yes. No lock-in contracts. Cancel anytime with a 14-day money-back guarantee for new subscribers.' },
];

const LandingPage = () => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const navLinks = ['Features', 'How It Works', 'Pricing', 'FAQ'];
  const scrollTo = (id) => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); };

  return (
    <ThemeProvider theme={landingTheme}>
    <Box sx={{ bgcolor: '#0A0E17', color: '#F9FAFB', overflow: 'hidden', minHeight: '100vh', '@keyframes metalReflection': { '0%': { backgroundPosition: '150% center, 0% center' }, '100%': { backgroundPosition: '-50% center, 0% center' } } }}>

      {/* ══════════════════ NAVBAR ══════════════════ */}
      <Box component="nav" sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', bgcolor: 'rgba(10,14,23,0.8)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <Box
              component="img"
              src="/assets/logo-animated.svg"
              alt="AITrader.gold"
              sx={{ height: 36 }}
            />
          </Box>
          {isMobile ? (
            <>
              <IconButton onClick={() => setMobileMenuOpen(true)} sx={{ color: '#F9FAFB' }}><MenuIcon /></IconButton>
              <Drawer anchor="right" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} PaperProps={{ sx: { bgcolor: '#111827', width: 280, pt: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}><IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: '#9CA3AF' }}><CloseIcon /></IconButton></Box>
                <List>{navLinks.map(l => <ListItem key={l} onClick={() => scrollTo(l.toLowerCase().replace(/\s+/g, ''))} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,255,148,0.05)' } }}><ListItemText primary={l} sx={{ color: '#F9FAFB' }} /></ListItem>)}</List>
                <Box sx={{ px: 2, mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button variant="outlined" component={RouterLink} to="/login" sx={{ borderColor: 'rgba(255,255,255,0.2)', color: '#F9FAFB' }}>Login</Button>
                  <Button variant="contained" component={RouterLink} to="/register">Start Free Trial</Button>
                </Box>
              </Drawer>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {navLinks.map(l => <Button key={l} onClick={() => scrollTo(l.toLowerCase().replace(/\s+/g, ''))} sx={{ color: '#9CA3AF', fontSize: '0.9rem', '&:hover': { color: '#F9FAFB', bgcolor: 'rgba(255,255,255,0.04)' } }}>{l}</Button>)}
              <Button variant="outlined" component={RouterLink} to="/login" sx={{ ml: 2, borderColor: 'rgba(255,255,255,0.2)', color: '#F9FAFB', '&:hover': { borderColor: '#00FF94', color: '#00FF94' } }}>Login</Button>
              <Button variant="contained" component={RouterLink} to="/register" sx={{ ml: 1 }}>Start Free Trial</Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* ══════════════════ CRYPTO TICKER (sticky under navbar) ══════════════════ */}
      <CryptoTicker />

      {/* ══════════════════ HERO ══════════════════ */}
      <Box sx={{ position: 'relative', pt: { xs: 18, md: 22 }, pb: { xs: 10, md: 16 }, minHeight: { md: '100vh' }, display: 'flex', alignItems: 'center' }}>
        <GlowOrb top="-10%" left="-10%" size={700} />
        <GlowOrb top="30%" left="70%" size={500} color="rgba(59,130,246,0.06)" />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <FadeSection>
                <Chip label="Powered by AI & Cloudflare Workers" sx={{ mb: 3, bgcolor: 'rgba(0,255,148,0.1)', color: '#00FF94', border: '1px solid rgba(0,255,148,0.2)', fontWeight: 600 }} icon={<RocketLaunch sx={{ color: '#00FF94 !important', fontSize: 16 }} />} />
                <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', sm: '3.2rem', md: '4rem', lg: '4.5rem' }, lineHeight: 1.1, mb: 3 }}>
                  Trade Smarter with{' '}
                  <Box component="span" sx={{ ...shinyGradient('linear-gradient(135deg, #00FF94, #3B82F6)') }}>
                    AI-Powered
                  </Box>
                  {' '}Precision
                </Typography>
                <Typography variant="h6" sx={{ color: '#9CA3AF', fontWeight: 400, lineHeight: 1.7, mb: 4, maxWidth: 560, fontSize: { xs: '1rem', md: '1.15rem' } }}>
                  13 indicators. 6 timeframes. Sub-second trailing stops. Rescue scale-in. 
                  Everything you need to build, deploy, and optimize autonomous trading strategies.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 4 }}>
                  <Button variant="contained" size="large" component={RouterLink} to="/register" endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, fontSize: '1.05rem' }}>
                    Start Free Trial
                  </Button>
                  <Button variant="outlined" size="large" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} sx={{ px: 4, py: 1.6, fontSize: '1.05rem', borderColor: 'rgba(255,255,255,0.15)', color: '#F9FAFB', '&:hover': { borderColor: '#00FF94', color: '#00FF94' } }}>
                    Explore Features
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {['No credit card required', '14-day free trial', 'Cancel anytime'].map(t => (
                    <Box key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                      <CheckCircle sx={{ fontSize: 16, color: '#00FF94' }} />
                      <Typography variant="body2" sx={{ color: '#9CA3AF' }}>{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </FadeSection>
            </Grid>
            <Grid item xs={12} md={5}>
              <FadeSection delay={0.2}>
                <Box sx={{ position: 'relative' }}>
                  {/* Glow background */}
                  <Box sx={{ position: 'absolute', inset: -2, background: 'linear-gradient(135deg, rgba(0,255,148,0.15), rgba(59,130,246,0.15))', borderRadius: 3, filter: 'blur(30px)' }} />
                  {/* Decorative chart lines behind the card */}
                  <Box sx={{ position: 'absolute', top: 0, right: -40, width: '70%', height: '100%', opacity: 0.08, pointerEvents: 'none', zIndex: 0 }}>
                    <svg viewBox="0 0 300 200" width="100%" height="100%" preserveAspectRatio="none">
                      <polyline points="0,150 40,120 80,140 120,90 160,110 200,70 240,85 300,40" fill="none" stroke="#00FF94" strokeWidth="2" />
                      <polyline points="0,170 40,150 80,165 120,130 160,145 200,110 240,125 300,80" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeDasharray="8,4" />
                    </svg>
                  </Box>
                  <GlassCard hover={false} sx={{ position: 'relative', p: 3, overflow: 'visible', zIndex: 1, boxShadow: '0 0 20px rgba(0,255,148,0.12)' }}>
                    {/* Platform overview header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2.5 }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF', mb: 0.5 }}>Platform Preview</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                          <LivePortfolioValue />
                          <Chip label="Demo" size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'rgba(0,255,148,0.12)', color: '#00FF94' }} />
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right', mr: { lg: 6 } }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF', mb: 0.5 }}>Trading Modes</Typography>
                        <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#00FF94' }}>
                          Paper + Live
                        </Typography>
                      </Box>
                    </Box>
                    {/* Animated area chart */}
                    <Box sx={{ mb: 2.5 }}>
                      <svg viewBox="0 0 400 100" width="100%" height="110" preserveAspectRatio="none" style={{ display: 'block' }}>
                        <defs>
                          <linearGradient id="heroPortFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#00FF94" stopOpacity="0.35" />
                            <stop offset="100%" stopColor="#00FF94" stopOpacity="0" />
                          </linearGradient>
                          <clipPath id="heroPortClip">
                            <rect x="0" y="0" width="400" height="100">
                              <animate attributeName="width" from="0" to="400" dur="2s" fill="freeze" calcMode="spline" keySplines="0.25 0.1 0.25 1" />
                            </rect>
                          </clipPath>
                        </defs>
                        {[25,50,75].map(y => <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />)}
                        <path clipPath="url(#heroPortClip)" d="M0,80 L40,72 L80,75 L120,55 L160,60 L200,40 L240,45 L280,25 L320,30 L360,15 L400,10 L400,100 L0,100 Z" fill="url(#heroPortFill)" />
                        <path clipPath="url(#heroPortClip)" d="M0,80 L40,72 L80,75 L120,55 L160,60 L200,40 L240,45 L280,25 L320,30 L360,15 L400,10" fill="none" stroke="#00FF94" strokeWidth="2.5" strokeLinecap="round" />
                        <circle cx="400" cy="10" r="4" fill="#00FF94" clipPath="url(#heroPortClip)">
                          <animate attributeName="r" values="3;6;3" dur="2s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
                        </circle>
                      </svg>
                    </Box>
                    {/* Stats grid */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2.5 }}>
                      {[
                        { label: 'Indicators', value: 13 },
                        { label: 'Timeframes', value: 6 },
                        { label: 'Presets', value: 5 },
                      ].map((s, i) => (
                        <Box key={i} sx={{ bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 2, p: 1.5 }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', mb: 0.3 }}>{s.label}</Typography>
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#F9FAFB' }}>
                            <CountUp value={s.value} duration={1700} />
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    {/* AI Signal bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.18)', borderRadius: 2, p: 1.5 }}>
                      <Box sx={{ width: 32, height: 32, bgcolor: 'rgba(0,255,148,0.15)', borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <TrendingUp sx={{ fontSize: 18, color: '#00FF94' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#F9FAFB' }}>Signal Matrix Ready</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                          Webhook, paper trading, and execution workflow available
                        </Typography>
                      </Box>
                    </Box>
                  </GlassCard>
                  {/* Floating badge — top right */}
                  <Box sx={{ position: 'absolute', top: -24, right: -24, bgcolor: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, px: 1.5, py: 1, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 2, display: { xs: 'none', lg: 'block' } }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#00FF94' }}>
                      Paper Mode
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF' }}>Risk-free testing</Typography>
                  </Box>
                  {/* Floating badge — bottom left */}
                  <Box sx={{ position: 'absolute', bottom: -16, left: -16, bgcolor: '#111827', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, px: 1.5, py: 1, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 2, display: { xs: 'none', lg: 'block' } }}>
                    <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF', mb: 0.5 }}>Automation Ready</Typography>
                    <Box sx={{ display: 'flex', gap: '3px' }}>
                      {[3,4,2,5].map((h, i) => (
                        <Box key={i} sx={{
                          width: 5, height: h * 4, borderRadius: 1,
                          bgcolor: i < 2 ? '#00FF94' : '#3B82F6',
                          animation: `heroBounce 1s ease-in-out ${i * 0.15}s infinite`,
                          '@keyframes heroBounce': { '0%,100%': { transform: 'scaleY(1)' }, '50%': { transform: 'scaleY(1.5)' } },
                        }} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </FadeSection>
            </Grid>
          </Grid>
        </Container>
        {/* Scroll indicator */}
        <Box sx={{ position: 'absolute', bottom: { xs: 16, md: 32 }, left: '50%', transform: 'translateX(-50%)', opacity: 0.35, textAlign: 'center' }}>
          <Box sx={{ width: 24, height: 40, border: '2px solid #6B7280', borderRadius: 3, mx: 'auto', display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: 4, height: 10, bgcolor: '#9CA3AF', borderRadius: 1, mt: 1, animation: 'scrollDot 1.8s ease-in-out infinite', '@keyframes scrollDot': { '0%,100%': { transform: 'translateY(0)', opacity: 0.4 }, '50%': { transform: 'translateY(8px)', opacity: 1 } } }} />
          </Box>
          <Typography sx={{ fontSize: '0.65rem', color: '#6B7280', mt: 1 }}>Scroll to explore</Typography>
        </Box>
      </Box>

      {/* ══════════════════ STATS BAR ══════════════════ */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)', py: 5 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} justifyContent="center">
            {[
              { num: '13', label: 'Technical Indicators' },
              { num: '6', label: 'Timeframes Analyzed' },
              { num: '<1s', label: 'Trailing Stop Latency' },
              { num: '5+', label: 'Strategy Presets' },
            ].map((s, i) => (
              <Grid item xs={6} md={3} key={i}>
                <FadeSection delay={i * 0.1}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800, fontFamily: '"Space Grotesk", sans-serif', ...shinyGradient('linear-gradient(135deg, #00FF94, #3B82F6)') }}>{s.num}</Typography>
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem', fontWeight: 500, mt: 0.5 }}>{s.label}</Typography>
                  </Box>
                </FadeSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ FEATURES ══════════════════ */}
      <Box id="features" sx={{ py: { xs: 10, md: 14 }, position: 'relative', overflow: 'hidden' }}>
        <AnimatedBackgroundLayer>
          <NeuralHeroBg />
        </AnimatedBackgroundLayer>
        <GlowOrb top="20%" left="80%" size={400} color="rgba(59,130,246,0.05)" />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip label="FEATURES" sx={{ mb: 2, bgcolor: 'rgba(0,255,148,0.1)', color: '#00FF94', border: '1px solid rgba(0,255,148,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Everything You Need to{' '}
                <Box component="span" sx={{ ...shinyText('#00FF94') }}>Trade Autonomously</Box>
              </Typography>
              <Typography sx={{ color: '#9CA3AF', maxWidth: 650, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
                Built for serious traders. 12 powerful modules working together to scan, enter, manage, protect, and optimize your positions.
              </Typography>
            </Box>
          </FadeSection>
          <Grid container spacing={3} alignItems="stretch">
            {features.map((f, i) => (
              <Grid item xs={12} sm={6} lg={4} key={i} sx={{ display: 'flex' }}>
                <FadeSection delay={i * 0.05} sx={{ display: 'flex', width: '100%' }}>
                  <GlassCard sx={{ height: '100%', width: '100%' }}>
                    <CardContent sx={{ p: 3.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{ width: 48, height: 48, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, rgba(0,255,148,0.15), rgba(0,255,148,0.05))', border: '1px solid rgba(0,255,148,0.1)' }}>
                          {React.cloneElement(f.icon, { sx: { color: '#00FF94', fontSize: 24 } })}
                        </Box>
                        <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>{f.title}</Typography>
                      </Box>
                      <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: 1.7 }}>{f.desc}</Typography>
                    </CardContent>
                  </GlassCard>
                </FadeSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ MULTI-AGENT PIPELINE ══════════════════ */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)', overflow: 'hidden' }}>
        <AnimatedBackgroundLayer>
          <ChartHeroBg />
        </AnimatedBackgroundLayer>
        <GlowOrb top="25%" left="60%" size={500} color="rgba(99,102,241,0.04)" />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip label="ARCHITECTURE" sx={{ mb: 2, bgcolor: 'rgba(99,102,241,0.1)', color: '#6366F1', border: '1px solid rgba(99,102,241,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Multi-Agent{' '}
                <Box component="span" sx={{ ...shinyText('#6366F1') }}>Pipeline</Box>
              </Typography>
              <Typography sx={{ color: '#9CA3AF', maxWidth: 620, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
                Each agent is a self-contained intelligence module. They collaborate through a typed message bus at the edge.
              </Typography>
            </Box>
          </FadeSection>
          <Grid container spacing={3} alignItems="stretch">
            {[
              { title: 'Signal Agent', desc: 'Multi-timeframe technical analysis. Processes RSI, MACD, Bollinger Bands, EMA crosses, and additional indicators across 6 timeframes.', color: '#6366F1', icon: <Bolt />, tags: ['RSI', 'MACD', 'ATR', 'EMA Stack', 'Volume'] },
              { title: 'Direction Agent', desc: 'HTF trend alignment with impulse-gate verification. No trade enters against the macro trend. Momentum confirmation required.', color: '#10B981', icon: <TrendingUp />, tags: ['HTF Trend', 'Impulse Gate', 'Micro Momentum'] },
              { title: 'Risk Agent', desc: 'Dynamic risk sizing. Portfolio-level drawdown caps. Capital tier gating keeps position growth controlled and rules-based.', color: '#FBBF24', icon: <Shield />, bars: [{ label: 'Max Drawdown', value: 'Configurable', width: '20%' }, { label: 'Position Size', value: 'Configurable', width: '35%' }] },
              { title: 'Execution Agent', desc: 'Smart order routing. Limit orders with slippage protection. Reactive trailing stops. Rescue scale-in for recovering positions.', color: '#EF4444', icon: <Speed />, tags: ['Limit', 'Market', 'Trailing', 'Rescue'] },
            ].map((agent, i) => (
              <Grid item xs={12} md={6} key={i}>
                <FadeSection delay={i * 0.1} style={{ height: '100%' }}>
                  <GlassCard sx={{ height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${agent.color}18`, border: `1px solid ${agent.color}30`, '& .MuiSvgIcon-root': { color: agent.color, fontSize: 22 } }}>
                          {agent.icon}
                        </Box>
                        {agent.metric && (
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>{agent.metricLabel}</Typography>
                            <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif', color: agent.color, lineHeight: 1 }}>
                              <CountUp value={agent.metric} prefix="" suffix="%" decimals={1} duration={1800} />
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.15rem', mb: 1 }}>{agent.title}</Typography>
                      <Typography sx={{ color: '#9CA3AF', fontSize: '0.88rem', lineHeight: 1.7, mb: 2, flex: 1 }}>{agent.desc}</Typography>
                      {agent.tags && (
                        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                          {agent.tags.map((t, ti) => (
                            <Chip key={ti} label={t} size="small" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600, bgcolor: `${agent.color}15`, color: agent.color, border: `1px solid ${agent.color}30` }} />
                          ))}
                        </Box>
                      )}
                      {agent.bars && (
                        <Box>
                          {agent.bars.map((bar, bi) => (
                            <Box key={bi} sx={{ mb: bi < agent.bars.length - 1 ? 1.5 : 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', mb: 0.5 }}>
                                <Typography sx={{ color: '#9CA3AF', fontSize: 'inherit' }}>{bar.label}</Typography>
                                <Typography sx={{ color: agent.color, fontSize: 'inherit', fontWeight: 600 }}>{bar.value}</Typography>
                              </Box>
                              <AnimatedBar width={bar.width} gradient={agent.color} delay={bi * 0.2} />
                            </Box>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </GlassCard>
                </FadeSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ NODE SYSTEM SHOWCASE ══════════════════ */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)', overflow: 'hidden' }}>
        <AnimatedBackgroundLayer>
          <ChartHeroBg />
        </AnimatedBackgroundLayer>
        <GlowOrb top="20%" left="10%" size={500} color="rgba(59,130,246,0.05)" />
        <GlowOrb top="60%" left="75%" size={400} color="rgba(0,255,148,0.04)" />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <FadeSection>
                <Chip label="STRATEGY BUILDER" sx={{ mb: 2, bgcolor: 'rgba(168,85,247,0.1)', color: '#A855F7', border: '1px solid rgba(168,85,247,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
                <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, mb: 3, lineHeight: 1.2 }}>
                  Build Strategies{' '}
                  <Box component="span" sx={{ ...shinyText('#A855F7') }}>Visually</Box>{' '}
                  with Drag & Drop Nodes
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '1.05rem', lineHeight: 1.8, mb: 4 }}>
                  Connect intelligent nodes to create sophisticated trading workflows — no coding required. Each node is a powerful module that processes data, generates signals, manages risk, or executes trades.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { label: '10+ Node Types', desc: 'Data sources, analyzers, conditions, execution, and more' },
                    { label: 'Visual Connections', desc: 'Draw edges between nodes to define your data flow' },
                    { label: 'Real-Time Processing', desc: 'Nodes execute every minute with live market data' },
                  ].map((item, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <CheckCircle sx={{ color: '#A855F7', fontSize: 20, mt: 0.3 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{item.label}</Typography>
                        <Typography sx={{ color: '#6B7280', fontSize: '0.85rem' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </FadeSection>
            </Grid>
            <Grid item xs={12} md={7}>
              <FadeSection delay={0.15}>
                {/* Node Flow Diagram — matches actual StrategyBuilder node style */}
                <Box sx={{ position: 'relative', p: { xs: 1.5, md: 2.5 }, bgcolor: 'rgba(10,14,23,0.9)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                  {/* Dot grid background */}
                  <Box sx={{ position: 'absolute', inset: 0, opacity: 0.12, backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.2) 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                  {/* Floating label */}
                  <Box sx={{ position: 'absolute', top: 10, right: 14, zIndex: 5 }}>
                    <Chip label="Strategy Builder" size="small" sx={{ bgcolor: 'rgba(0,255,148,0.1)', color: '#00FF94', border: '1px solid rgba(0,255,148,0.2)', fontSize: '0.65rem', height: 22 }} />
                  </Box>

                  {/* Responsive node grid — stacks nicely on mobile */}
                  <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 }, py: 1 }}>
                    {/* Row 1: Data Source → Technical Analyst → Condition */}
                    <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                      {[
                        { label: 'Data Source', sub: 'BTC, ETH, SOL', color: '#6B7280', chip: 'KuCoin', icon: <DataObject /> },
                        { label: 'Technical Analyst', sub: 'RSI, MACD, BB, ATR', color: '#3b82f6', chip: 'Multi-TF', icon: <TrendingUp /> },
                        { label: 'Condition Gate', sub: 'RSI > 70 AND MACD < 0', color: '#F97316', chip: 'AND/OR', icon: <Timeline /> },
                      ].map((n, ni) => (
                        <React.Fragment key={ni}>
                          {ni > 0 && <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', color: n.color, mx: -0.5, flexShrink: 0 }}>
                            <svg width="36" height="2" style={{ overflow: 'visible' }}><line x1="0" y1="1" x2="28" y2="1" stroke={`${n.color}60`} strokeWidth="2" strokeDasharray="4,3"><animate attributeName="stroke-dashoffset" from="14" to="0" dur="1.5s" repeatCount="indefinite" /></line></svg>
                            <ArrowForward sx={{ fontSize: 14, color: `${n.color}80` }} />
                          </Box>}
                          <Box sx={{
                            flex: { xs: '1 1 calc(50% - 8px)', md: 1 }, minWidth: { xs: 140, md: 0 }, position: 'relative', overflow: 'hidden',
                            background: 'rgba(13,17,23,0.85)', backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255,255,255,0.10)', borderRadius: 2,
                            boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 12px ${n.color}12`,
                            transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.20)', boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${n.color}25`, transform: 'translateY(-2px)' },
                          }}>
                            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: n.color, boxShadow: `0 0 12px ${n.color}`, opacity: 0.8 }} />
                            <Box sx={{ position: 'absolute', right: -16, bottom: -16, width: 60, height: 60, background: `${n.color}18`, borderRadius: '50%', filter: 'blur(16px)' }} />
                            <Box sx={{ p: 1.5, position: 'relative', zIndex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.8 }}>
                                <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,17,23,0.5)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: `0 0 16px ${n.color}40, inset 0 0 8px ${n.color}10`, mr: 1, '& .MuiSvgIcon-root': { fontSize: 16, color: n.color, filter: `drop-shadow(0 0 6px ${n.color})` } }}>
                                  {n.icon}
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#fff', letterSpacing: '-0.01em' }}>{n.label}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.62rem', color: '#6B7280', fontFamily: '"JetBrains Mono", monospace', pl: 0.5, mb: 0.5 }}>{n.sub}</Typography>
                              <Chip label={n.chip} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: `${n.color}18`, color: n.color, border: `1px solid ${n.color}30` }} />
                            </Box>
                            <Box sx={{ position: 'absolute', top: 18, left: -4, width: 8, height: 8, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.15)' }} />
                            <Box sx={{ position: 'absolute', top: 18, right: -4, width: 8, height: 8, borderRadius: 0.5, bgcolor: n.color, boxShadow: `0 0 6px ${n.color}` }} />
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>

                    {/* Row 2: Risk Manager → Report Synth → Execution */}
                    <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                      {[
                        { label: 'Risk Manager', sub: 'SL 1.5% • TP 2.5% • 5×', color: '#00FF94', chip: 'Leverage-Aware', icon: <Shield /> },
                        { label: 'Report Synth', sub: 'Weighted consensus', color: '#A855F7', chip: 'Conflict Resolver', icon: <Insights /> },
                        { label: 'Execution Layer', sub: 'Market • Paper Mode', color: '#00FF94', chip: 'Auto-Execute', icon: <Bolt /> },
                      ].map((n, ni) => (
                        <React.Fragment key={ni}>
                          {ni > 0 && <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', color: n.color, mx: -0.5, flexShrink: 0 }}>
                            <svg width="36" height="2" style={{ overflow: 'visible' }}><line x1="0" y1="1" x2="28" y2="1" stroke={`${n.color}60`} strokeWidth="2" strokeDasharray="4,3"><animate attributeName="stroke-dashoffset" from="14" to="0" dur="1.5s" repeatCount="indefinite" /></line></svg>
                            <ArrowForward sx={{ fontSize: 14, color: `${n.color}80` }} />
                          </Box>}
                          <Box sx={{
                            flex: { xs: '1 1 calc(50% - 8px)', md: 1 }, minWidth: { xs: 140, md: 0 }, position: 'relative', overflow: 'hidden',
                            background: 'rgba(13,17,23,0.85)', backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255,255,255,0.10)', borderRadius: 2,
                            boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 12px ${n.color}12`,
                            transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.20)', boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${n.color}25`, transform: 'translateY(-2px)' },
                          }}>
                            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: n.color, boxShadow: `0 0 12px ${n.color}`, opacity: 0.8 }} />
                            <Box sx={{ position: 'absolute', right: -16, bottom: -16, width: 60, height: 60, background: `${n.color}18`, borderRadius: '50%', filter: 'blur(16px)' }} />
                            <Box sx={{ p: 1.5, position: 'relative', zIndex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.8 }}>
                                <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,17,23,0.5)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: `0 0 16px ${n.color}40, inset 0 0 8px ${n.color}10`, mr: 1, '& .MuiSvgIcon-root': { fontSize: 16, color: n.color, filter: `drop-shadow(0 0 6px ${n.color})` } }}>
                                  {n.icon}
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#fff', letterSpacing: '-0.01em' }}>{n.label}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.62rem', color: '#6B7280', fontFamily: '"JetBrains Mono", monospace', pl: 0.5, mb: 0.5 }}>{n.sub}</Typography>
                              <Chip label={n.chip} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: `${n.color}18`, color: n.color, border: `1px solid ${n.color}30` }} />
                            </Box>
                            <Box sx={{ position: 'absolute', top: 18, left: -4, width: 8, height: 8, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.15)' }} />
                            <Box sx={{ position: 'absolute', top: 18, right: -4, width: 8, height: 8, borderRadius: 0.5, bgcolor: n.color, boxShadow: `0 0 6px ${n.color}` }} />
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>

                    {/* Row 3: Scheduler + Rescue Manager */}
                    <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                      {[
                        { label: 'Scheduler', sub: 'Every 1min • Confidence 82%', color: '#EF4444', chip: 'Cron Active', icon: <Speed /> },
                        { label: 'Rescue Manager', sub: '4-level ladder • AI gate', color: '#EF4444', chip: 'Scale-In', icon: <Security /> },
                        { label: 'Signal Matrix', sub: '13 indicators • Weighted', color: '#00FF94', chip: '6 Timeframes', icon: <Insights /> },
                      ].map((n, ni) => (
                        <React.Fragment key={ni}>
                          {ni > 0 && <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', color: n.color, mx: -0.5, flexShrink: 0 }}>
                            <svg width="36" height="2" style={{ overflow: 'visible' }}><line x1="0" y1="1" x2="28" y2="1" stroke={`${n.color}60`} strokeWidth="2" strokeDasharray="4,3"><animate attributeName="stroke-dashoffset" from="14" to="0" dur="1.5s" repeatCount="indefinite" /></line></svg>
                            <ArrowForward sx={{ fontSize: 14, color: `${n.color}80` }} />
                          </Box>}
                          <Box sx={{
                            flex: { xs: '1 1 calc(50% - 8px)', md: 1 }, minWidth: { xs: 140, md: 0 }, position: 'relative', overflow: 'hidden',
                            background: 'rgba(13,17,23,0.85)', backdropFilter: 'blur(16px)',
                            border: '1px solid rgba(255,255,255,0.10)', borderRadius: 2,
                            boxShadow: `0 4px 24px rgba(0,0,0,0.4), 0 0 12px ${n.color}12`,
                            transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                            '&:hover': { borderColor: 'rgba(255,255,255,0.20)', boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${n.color}25`, transform: 'translateY(-2px)' },
                          }}>
                            <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: n.color, boxShadow: `0 0 12px ${n.color}`, opacity: 0.8 }} />
                            <Box sx={{ position: 'absolute', right: -16, bottom: -16, width: 60, height: 60, background: `${n.color}18`, borderRadius: '50%', filter: 'blur(16px)' }} />
                            <Box sx={{ p: 1.5, position: 'relative', zIndex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.8 }}>
                                <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(13,17,23,0.5)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: `0 0 16px ${n.color}40, inset 0 0 8px ${n.color}10`, mr: 1, '& .MuiSvgIcon-root': { fontSize: 16, color: n.color, filter: `drop-shadow(0 0 6px ${n.color})` } }}>
                                  {n.icon}
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#fff', letterSpacing: '-0.01em' }}>{n.label}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.62rem', color: '#6B7280', fontFamily: '"JetBrains Mono", monospace', pl: 0.5, mb: 0.5 }}>{n.sub}</Typography>
                              <Chip label={n.chip} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: `${n.color}18`, color: n.color, border: `1px solid ${n.color}30` }} />
                            </Box>
                            <Box sx={{ position: 'absolute', top: 18, left: -4, width: 8, height: 8, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.15)' }} />
                            <Box sx={{ position: 'absolute', top: 18, right: -4, width: 8, height: 8, borderRadius: 0.5, bgcolor: n.color, boxShadow: `0 0 6px ${n.color}` }} />
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </FadeSection>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ HOW IT WORKS ══════════════════ */}
      <Box id="howitworks" sx={{ py: { xs: 10, md: 14 }, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <Container maxWidth="lg">
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip label="HOW IT WORKS" sx={{ mb: 2, bgcolor: 'rgba(59,130,246,0.1)', color: '#3B82F6', border: '1px solid rgba(59,130,246,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                From Strategy to Profit in{' '}
                <Box component="span" sx={{ ...shinyText('#3B82F6') }}>4 Steps</Box>
              </Typography>
            </Box>
          </FadeSection>
          <Grid container spacing={4}>
            {steps.map((s, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <FadeSection delay={i * 0.12}>
                  <Box sx={{ textAlign: 'center', position: 'relative' }}>
                    <Typography sx={{ fontSize: '4rem', fontWeight: 900, fontFamily: '"Space Grotesk", sans-serif', lineHeight: 1, mb: 2, ...shinyGradient('linear-gradient(180deg, rgba(0,255,148,0.3), transparent)') }}>{s.num}</Typography>
                    <Typography variant="h6" sx={{ mb: 1.5, fontSize: '1.1rem' }}>{s.title}</Typography>
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: 1.7 }}>{s.desc}</Typography>
                  </Box>
                </FadeSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ DECISION TRACE ══════════════════ */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)', overflow: 'hidden' }}>
        <AnimatedBackgroundLayer>
          <CodeHeroBg />
        </AnimatedBackgroundLayer>
        <GlowOrb top="40%" left="60%" size={400} color="rgba(0,255,148,0.04)" />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip label="UNDER THE HOOD" sx={{ mb: 2, bgcolor: 'rgba(0,255,148,0.1)', color: '#00FF94', border: '1px solid rgba(0,255,148,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Decision{' '}
                <Box component="span" sx={{ ...shinyText('#00FF94') }}>Trace</Box>
              </Typography>
            </Box>
          </FadeSection>
          {(() => {
            const codeLines = [
              <><span className="kw">async</span> <span className="fn">evaluateEntry</span><span style={{ color: '#6B7280' }}>(signal: Signal) {'{'}</span></>,
              <><span className="cmt">{'  // 1. Direction check — 0.02ms'}</span></>,
              <>{'  '}<span className="kw">const</span>{' dir = '}<span className="kw">await</span>{' '}<span className="fn">this</span>.directionStack.<span style={{ color: '#FBBF24' }}>evaluate</span>(signal);</>,
              <>{'  '}<span className="kw">if</span>{' (!dir.aligned) '}<span className="kw">return</span>{' '}<span className="fn">this</span>.<span style={{ color: '#FBBF24' }}>reject</span>(<span className="str">'HTF_MISALIGN'</span>);</>,
              <>&nbsp;</>,
              <><span className="cmt">{'  // 2. Risk validation — 0.05ms'}</span></>,
              <>{'  '}<span className="kw">const</span>{' risk = '}<span className="kw">await</span>{' '}<span className="fn">this</span>.riskAgent.<span style={{ color: '#FBBF24' }}>validate</span>({'{'});</>,
              <>{'    capital: '}<span className="fn">this</span>.portfolio.available,</>,
              <>{'    maxDrawdown: '}<span className="num">0.02</span>,</>,
              <>{'    riskFraction: '}<span className="num">0.25</span>,</>,
              <>{'  '}{'}'})</>,
              <>&nbsp;</>,
              <><span className="cmt">{'  // 3. Entry quality gate — 0.03ms'}</span></>,
              <>{'  '}<span className="kw">const</span>{' quality = '}<span className="fn">this</span>.entryGate.<span style={{ color: '#FBBF24' }}>score</span>(signal);</>,
              <>{'  '}<span className="kw">if</span>{' (quality < '}<span className="num">0.7</span>{') '}<span className="kw">return</span>{' '}<span className="fn">this</span>.<span style={{ color: '#FBBF24' }}>reject</span>(<span className="str">'LOW_QUALITY'</span>);</>,
              <>&nbsp;</>,
              <><span className="cmt">{'  // 4. Execute with protection — 0.2ms'}</span></>,
              <>{'  '}<span className="kw">return</span>{' '}<span className="fn">this</span>.executor.<span style={{ color: '#FBBF24' }}>placeOrder</span>({'{'});</>,
              <>{'    symbol: signal.symbol,'}</>,
              <>{'    side: dir.direction,'}</>,
              <>{'    size: risk.positionSize,'}</>,
              <>{'    stopLoss: risk.dynamicStop,'}</>,
              <>{'    trailing: { activation: '}<span className="num">0.5</span>{', step: '}<span className="num">0.1</span>{' },'};</>,
              <>{'  '}{'}'});{' '}<span className="cmt">{'// total: ~0.3ms'}</span></>,
              <><span style={{ color: '#6B7280' }}>{'}'}</span></>,
            ];
            return (
              <CodeScrollReveal>
                <GlassCard sx={{ overflow: 'hidden', background: 'rgba(10,14,26,0.95)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.5, borderBottom: '1px solid rgba(255,255,255,0.04)', bgcolor: 'rgba(0,0,0,0.2)' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#EF4444' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#EAB308' }} />
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22C55E' }} />
                    <Typography sx={{ ml: 1.5, fontSize: '0.75rem', color: '#6B7280', fontFamily: '"JetBrains Mono", monospace' }}>execution-engine.ts</Typography>
                  </Box>
                  <Box sx={{ p: { xs: 2, md: 3 }, fontFamily: '"JetBrains Mono", monospace', fontSize: { xs: '0.7rem', md: '0.82rem' }, lineHeight: 2, overflowX: 'auto', '& .kw': { color: '#C084FC' }, '& .fn': { color: '#60A5FA' }, '& .str': { color: '#4ADE80' }, '& .num': { color: '#FB923C' }, '& .cmt': { color: '#6B7280' } }}>
                    {codeLines.map((line, i) => (
                      <Box key={i} className="code-line-scroll" sx={{ opacity: 0, transform: 'translateY(10px)', transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)', whiteSpace: 'pre-wrap', '&.visible': { opacity: 1, transform: 'translateY(0)' } }}>
                        {line}
                      </Box>
                    ))}
                  </Box>
                </GlassCard>
              </CodeScrollReveal>
            );
          })()}
        </Container>
      </Box>

      {/* ══════════════════ PERFORMANCE ══════════════════ */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', bgcolor: 'rgba(17,24,39,0.3)', borderTop: '1px solid rgba(255,255,255,0.04)', overflow: 'hidden' }}>
        <AnimatedBackgroundLayer>
          <CodeHeroBg />
        </AnimatedBackgroundLayer>
        <GlowOrb top="30%" left="20%" size={500} color="rgba(0,255,148,0.04)" />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 6, lg: 8 }} alignItems="center">
            {/* Left — Stats */}
            <Grid item xs={12} lg={6}>
              <FadeSection>
                <Chip label="PERFORMANCE" sx={{ mb: 2, bgcolor: 'rgba(0,255,148,0.1)', color: '#00FF94', border: '1px solid rgba(0,255,148,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
                <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, mb: 2, lineHeight: 1.2 }}>
                  Platform{' '}
                  <Box component="span" sx={{ ...shinyText('#00FF94') }}>Analytics Dashboard</Box>
                </Typography>
                <Typography sx={{ color: '#9CA3AF', fontSize: '1.05rem', lineHeight: 1.8, mb: 4 }}>
                  Review strategy equity curves, drawdown snapshots, and trade analytics inside the platform dashboard with transparent benchmark-style visualizations.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {[
                    { label: 'Signal Matrix Coverage', value: '13', color: '#00FF94', width: '75%', gradient: 'linear-gradient(90deg, #00FF94, #3B82F6)' },
                    { label: 'Timeframe Analysis', value: '6', color: '#9CA3AF', width: '55%', gradient: 'linear-gradient(90deg, #4B5563, #6B7280)' },
                    { label: 'Strategy Presets', value: '5', color: '#FBBF24', width: '50%', gradient: 'linear-gradient(90deg, #FBBF24, #F59E0B)' },
                    { label: 'Trailing Engine', value: '1', color: '#3B82F6', width: '60%', gradient: 'linear-gradient(90deg, #3B82F6, #6366F1)' },
                  ].map((bar, i) => (
                    <Box key={i}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.85rem', color: '#D1D5DB' }}>{bar.label}</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: bar.color, fontFamily: '"JetBrains Mono", monospace' }}>
                          {bar.value}
                        </Typography>
                      </Box>
                      <AnimatedBar width={bar.width} gradient={bar.gradient} delay={i * 0.15} />
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', mt: 3 }}>
                  Illustrations are for product demonstration only and should not be interpreted as guaranteed or historical investment results.
                </Typography>
              </FadeSection>
            </Grid>
            {/* Right — Equity Curve Chart */}
            <Grid item xs={12} lg={6}>
              <FadeSection delay={0.2}>
                <PerformanceChartCard />
              </FadeSection>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ SPEED OF LIGHT — INFRASTRUCTURE ══════════════════ */}
      <Box sx={{ py: { xs: 14, md: 20 }, position: 'relative', overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.04)',
        background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(0,255,148,0.06) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(59,130,246,0.04) 0%, transparent 50%)' }}>
        <AnimatedBackgroundLayer>
          <SpeedHeroBg />
        </AnimatedBackgroundLayer>
        <GlowOrb top="20%" left="-5%" size={500} color="rgba(0,255,148,0.05)" />
        <GlowOrb top="60%" left="80%" size={400} color="rgba(59,130,246,0.04)" />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center' }}>
              {/* Badge */}
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.7, borderRadius: 100, border: '1px solid rgba(0,255,148,0.2)', bgcolor: 'rgba(0,255,148,0.06)', mb: 4 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#00FF94', boxShadow: '0 0 8px #00FF94', animation: 'dotPulse 2s ease-in-out infinite', '@keyframes dotPulse': { '0%,100%': { opacity: 0.5, transform: 'scale(1)' }, '50%': { opacity: 1, transform: 'scale(1.3)' } } }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#00FF94' }}>Edge Infrastructure</Typography>
              </Box>
              {/* Title */}
              <Typography variant="h1" sx={{ fontSize: { xs: '2.8rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' }, lineHeight: 1.05, mb: 3, color: 'rgba(255,255,255,0.85)' }}>
                Trade at the<br />
                <Box component="span" sx={{ ...shinyGradient('linear-gradient(135deg, #00FF94, #FBBF24)'), fontSize: 'inherit' }}>
                  Speed of Light
                </Box>
              </Typography>
              {/* Description */}
              <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: '#9CA3AF', maxWidth: 560, mx: 'auto', lineHeight: 1.7, mb: 6 }}>
                Four core execution agents. 300+ edge locations. Sub-second automation where it matters. Our Cloudflare Workers architecture is designed for globally distributed execution.
              </Typography>
              {/* Stats */}
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 4, px: { xs: 1, md: 1 }, py: 3, backdropFilter: 'blur(12px)' }}>
                {[
                  { value: '1', suffix: 'm', label: 'CRON LOOP', color: '#3B82F6' },
                  { value: '300', suffix: '+', label: 'EDGE POPS', color: '#00FF94' },
                  { value: '2', suffix: '', label: 'DURABLE OBJECTS', color: '#FBBF24' },
                ].map((stat, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <Box sx={{ width: 1, height: 48, bgcolor: 'rgba(255,255,255,0.06)', mx: { xs: 2, md: 0 } }} />}
                    <Box sx={{ textAlign: 'center', px: { xs: 2.5, md: 4 } }}>
                      <Typography sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif', ...shinyText(stat.color), lineHeight: 1 }}>
                        <CountUp value={stat.value} decimals={(stat.value.split('.')[1] || '').length} duration={1800} />
                        <Box component="span" sx={{ fontSize: '0.7em' }}>{stat.suffix}</Box>
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#6B7280', mt: 0.7, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{stat.label}</Typography>
                    </Box>
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </FadeSection>
        </Container>
      </Box>

      {/* ══════════════════ POWERED BY CLOUDFLARE EDGE ══════════════════ */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)', overflow: 'hidden' }}>
        <AnimatedBackgroundLayer>
          <InfraHeroBg />
        </AnimatedBackgroundLayer>
        <GlowOrb top="20%" left="70%" size={400} color="rgba(249,115,22,0.04)" />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip label="INFRASTRUCTURE" sx={{ mb: 2, bgcolor: 'rgba(249,115,22,0.1)', color: '#F97316', border: '1px solid rgba(249,115,22,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Powered by{' '}
                <Box component="span" sx={{ ...shinyText('#F97316') }}>Cloudflare Edge</Box>
              </Typography>
              <Typography sx={{ color: '#9CA3AF', maxWidth: 600, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
                Zero cold starts. Global distribution. Every component runs within 50ms of your users.
              </Typography>
            </Box>
          </FadeSection>
          <Grid container spacing={3} alignItems="stretch">
            {[
              { title: 'Workers', desc: 'V8 isolate runtime. Strategy engine, cron triggers, API routing — all serverless at the edge.', color: '#F97316', icon: <Bolt />, stats: [{ val: '0ms', label: 'Cold start' }, { val: '300+', label: 'Regions' }] },
              { title: 'D1 Database', desc: 'SQLite at the edge. Strategies, trades, audit logs, ML parameters. Globally replicated.', color: '#3B82F6', icon: <DataObject />, stats: [{ val: '15+', label: 'Tables' }, { val: '<1ms', label: 'Read latency' }] },
              { title: 'Durable Objects', desc: 'StrategyRunDO + ReactiveTrailingDO. Per-strategy persistent state machines with strong consistency.', color: '#A855F7', icon: <Hub />, tags: ['StrategyRunDO', 'ReactiveTrailingDO'] },
              { title: 'R2 Storage', desc: 'Strategy logs, backtest artifacts, audit packages. S3-compatible object store.', color: '#06B6D4', icon: <Layers />, stats: [{ val: '$0', label: 'Egress fees' }, { val: 'S3', label: 'Compatible' }] },
              { title: 'KV Namespace', desc: 'Config cache, feature flags, encrypted API credentials. Globally replicated key-value store.', color: '#10B981', icon: <Security />, stats: [{ val: '~1ms', label: 'Read globally' }, { val: 'E.C.', label: 'Write model' }] },
              { title: 'Pages + React', desc: 'MUI trading dashboard, visual node-based strategy builder, admin panel. Deployed on Cloudflare Pages.', color: '#38BDF8', icon: <Devices />, tags: ['React 18', 'Material UI', 'CF Pages'] },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <FadeSection delay={i * 0.07} style={{ height: '100%' }}>
                  <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${item.color}18`, border: `1px solid ${item.color}30`, mb: 2, '& .MuiSvgIcon-root': { color: item.color, fontSize: 22 } }}>
                        {item.icon}
                      </Box>
                      <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>{item.title}</Typography>
                      <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem', lineHeight: 1.7, mb: 2.5, flex: 1 }}>{item.desc}</Typography>
                      {item.stats && (
                        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center' }}>
                          <Box sx={{ flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif', color: item.color }}>
                              {(() => {
                                const parsed = parseAnimatedMetric(item.stats[0].val);
                                return parsed ? <CountUp value={parsed.value} prefix={parsed.prefix} suffix={parsed.suffix} decimals={parsed.decimals} grouping={parsed.grouping} duration={1600} /> : item.stats[0].val;
                              })()}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>{item.stats[0].label}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, mx: 1.5 }}>
                            <AnimatedBar width="65%" gradient={`${item.color}60`} delay={0.3} />
                          </Box>
                          <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, fontFamily: '"Space Grotesk", sans-serif', color: item.color }}>
                              {(() => {
                                const parsed = parseAnimatedMetric(item.stats[1].val);
                                return parsed ? <CountUp value={parsed.value} prefix={parsed.prefix} suffix={parsed.suffix} decimals={parsed.decimals} grouping={parsed.grouping} duration={1600} /> : item.stats[1].val;
                              })()}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: '#6B7280' }}>{item.stats[1].label}</Typography>
                          </Box>
                        </Box>
                      )}
                      {item.tags && (
                        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                          {item.tags.map((t, ti) => (
                            <Chip key={ti} label={t} size="small" sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600, bgcolor: `${item.color}15`, color: item.color, border: `1px solid ${item.color}30` }} />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </GlassCard>
                </FadeSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ MARKET SESSIONS TIMETABLE ══════════════════ */}
      <Box sx={{ py: { xs: 12, md: 18 }, position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)', overflow: 'hidden', minHeight: { xs: 700, md: 900 } }}>
        <GlowOrb top="30%" left="60%" size={500} color="rgba(168,85,247,0.04)" />
        {/* World Map Background — Canvas animated from source */}
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, height: { xs: 500, md: 650 }, zIndex: 0,
          WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 70%, transparent 100%)',
          maskImage: 'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 70%, transparent 100%)',
        }}>
          <WorldMapCanvas />
        </Box>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 8, mt: '-120px' }}>
              <Chip label="GLOBAL MARKETS" sx={{ mb: 2, bgcolor: 'rgba(168,85,247,0.1)', color: '#A855F7', border: '1px solid rgba(168,85,247,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Trade Across All{' '}
                <Box component="span" sx={{ ...shinyText('#A855F7') }}>Market Sessions</Box>
              </Typography>
              <Typography sx={{ color: '#9CA3AF', maxWidth: 600, mx: 'auto', fontSize: '1.05rem' }}>
                Our AI monitors global market sessions 24/7, optimizing entries based on volatility windows and session overlaps.
              </Typography>
            </Box>
          </FadeSection>
          <FadeSection delay={0.1}>
            <Box sx={{ mt: '150px' }}>
              <MarketSessionsMonitor />
            </Box>
          </FadeSection>
        </Container>
      </Box>

      {/* ══════════════════ SUPPORTED EXCHANGES ══════════════════ */}
      <Box sx={{ py: { xs: 8, md: 12 }, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <Container maxWidth="lg">
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Chip label="INTEGRATIONS" sx={{ mb: 2, bgcolor: 'rgba(0,255,148,0.1)', color: '#00FF94', border: '1px solid rgba(0,255,148,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Connected to Top{' '}
                <Box component="span" sx={{ ...shinyText('#00FF94') }}>Exchanges</Box>
              </Typography>
              <Typography sx={{ color: '#9CA3AF', maxWidth: 550, mx: 'auto', fontSize: '1.05rem' }}>
                Trade on the world's leading crypto exchanges through our secure API integration.
              </Typography>
            </Box>
          </FadeSection>
          <Grid container spacing={3} justifyContent="center">
            {[
              { name: 'KuCoin', status: 'Live', desc: 'Spot, Margin & Futures', color: '#23AF91',
                svg: <svg viewBox="0 0 32 32" width="28" height="28"><path d="M16 4l-6 6v12l6 6 6-6v-4l-6 6-4-4V12l4-4 6 6V10z" fill="#23AF91"/><path d="M22 14v4l-6 6 6-6z" fill="#23AF91" opacity="0.7"/></svg> },
              { name: 'Binance', status: 'Coming Soon', desc: 'Spot & Futures', color: '#F0B90B',
                svg: <svg viewBox="0 0 32 32" width="28" height="28"><path d="M16 4l3 3-7 7-3-3zm-8 8l3 3-3 3-3-3zm8 0l3 3-7 7-3-3zm8 0l3 3-3 3-3-3zm-8 8l3 3-7 7-3-3z" fill="#F0B90B"/></svg> },
              { name: 'Bybit', status: 'Coming Soon', desc: 'Derivatives & Spot', color: '#F7A600',
                svg: <svg viewBox="0 0 32 32" width="28" height="28"><rect x="6" y="8" width="4" height="16" rx="1" fill="#F7A600"/><rect x="12" y="12" width="4" height="12" rx="1" fill="#F7A600"/><rect x="18" y="6" width="4" height="20" rx="1" fill="#F7A600"/><rect x="24" y="10" width="4" height="14" rx="1" fill="#F7A600" opacity="0.7"/></svg> },
              { name: 'OKX', status: 'Coming Soon', desc: 'Spot & Futures', color: '#FFFFFF',
                svg: <svg viewBox="0 0 32 32" width="28" height="28"><rect x="4" y="4" width="10" height="10" rx="2" fill="white"/><rect x="18" y="4" width="10" height="10" rx="2" fill="white"/><rect x="4" y="18" width="10" height="10" rx="2" fill="white"/><rect x="18" y="18" width="10" height="10" rx="2" fill="white" opacity="0.5"/></svg> },
              { name: 'Coinbase', status: 'Planned', desc: 'Advanced Trade API', color: '#0052FF',
                svg: <svg viewBox="0 0 32 32" width="28" height="28"><circle cx="16" cy="16" r="12" fill="#0052FF"/><path d="M16 10c-3.3 0-6 2.7-6 6s2.7 6 6 6 6-2.7 6-6h-4c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2v-4z" fill="white"/></svg> },
              { name: 'Kraken', status: 'Planned', desc: 'Spot & Margin', color: '#5741D9',
                svg: <svg viewBox="0 0 32 32" width="28" height="28"><circle cx="16" cy="16" r="12" fill="#5741D9"/><path d="M16 8v8l6 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none"/><circle cx="16" cy="16" r="2" fill="white"/></svg> },
            ].map((ex, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <FadeSection delay={i * 0.08}>
                  <GlassCard sx={{ height: '100%', textAlign: 'center', py: 3, px: 2, opacity: ex.status === 'Live' ? 1 : 0.6, transition: 'all 0.3s', '&:hover': { opacity: 1 } }}>
                    <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.08)', '& svg': { filter: ex.status !== 'Live' ? 'grayscale(0.5) opacity(0.7)' : 'none' } }}>
                      {ex.svg}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.3 }}>{ex.name}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#6B7280', mb: 1 }}>{ex.desc}</Typography>
                    <Chip label={ex.status} size="small" sx={{
                      height: 20, fontSize: '0.6rem', fontWeight: 700,
                      bgcolor: ex.status === 'Live' ? 'rgba(0,255,148,0.12)' : ex.status === 'Coming Soon' ? 'rgba(251,191,36,0.1)' : 'rgba(107,114,128,0.15)',
                      color: ex.status === 'Live' ? '#00FF94' : ex.status === 'Coming Soon' ? '#FBBF24' : '#9CA3AF',
                      border: `1px solid ${ex.status === 'Live' ? 'rgba(0,255,148,0.2)' : ex.status === 'Coming Soon' ? 'rgba(251,191,36,0.2)' : 'rgba(107,114,128,0.2)'}`,
                    }} />
                  </GlassCard>
                </FadeSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ 22-PHASE PROTECTION ══════════════════ */}
      <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)',
        '@keyframes dashFlow': { '0%': { maskPosition: '0 0', WebkitMaskPosition: '0 0' }, '100%': { maskPosition: '0 16px', WebkitMaskPosition: '0 16px' } },
        '@keyframes cardShine': { '0%': { backgroundPosition: '200% center' }, '100%': { backgroundPosition: '-50% center' } },
      }}>
        <GlowOrb top="30%" left="40%" size={500} color="rgba(0,255,148,0.04)" />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
              <Chip label="SAFETY" sx={{ mb: 2, bgcolor: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Execution{' '}
                <Box component="span" sx={{ ...shinyText('#10B981') }}>Safeguards</Box>
              </Typography>
              <Typography sx={{ color: '#9CA3AF', maxWidth: 560, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.7 }}>
                Every strategy run passes through multiple layered checks before live execution.
              </Typography>
            </Box>
          </FadeSection>
          {/* Timeline */}
          <Box sx={{ position: 'relative', pl: { xs: '56px', md: '72px' },
            '&::before': {
              content: '""', position: 'absolute', left: { xs: '15px', md: '19px' }, top: 0, bottom: 0, width: 2, zIndex: 1,
              background: 'linear-gradient(to bottom, #ef4444 0%, #d4af37 20%, #6366f1 40%, #10b981 60%, #d4af37 80%, #6366f1 100%)',
              WebkitMaskImage: 'repeating-linear-gradient(to bottom, white 0px, white 8px, transparent 8px, transparent 16px)',
              maskImage: 'repeating-linear-gradient(to bottom, white 0px, white 8px, transparent 8px, transparent 16px)',
              WebkitMaskSize: '2px 16px', maskSize: '2px 16px',
              animation: 'dashFlow 1.5s linear infinite',
            }
          }}>
            {[
              { num: '01', title: 'Kill Switch', desc: 'One-click emergency halt. Closes all positions, cancels all orders, freezes all strategies instantly.', color: '#EF4444' },
              { num: '02', title: 'Capital Growth Gates', desc: 'System must prove profitability at each tier before unlocking larger position sizes. No shortcuts.', color: '#FBBF24' },
              { num: '03', title: 'Dual Approval', desc: 'High-risk operations need both AI recommendation and human confirmation. RBAC-controlled permissions.', color: '#6366F1' },
              { num: '04', title: 'Paper Trading Validation', desc: 'Every strategy runs in shadow mode first. Must pass performance benchmarks before live capital is at risk.', color: '#10B981' },
              { num: '05', title: 'Broker Resilience', desc: 'Auto failover, rate limiting, balance reconciliation, and fee-aware execution across all exchanges.', color: '#FBBF24' },
              { num: '06', title: 'Human Safety Override', desc: 'Manual pause, stop, and operator review controls for sensitive strategy actions and live execution.', color: '#6366F1' },
            ].map((phase, i) => (
              <FadeSection key={i} delay={i * 0.1}>
                <Box sx={{
                  position: 'relative', pb: 3,
                  '&:hover .timeline-dot': { boxShadow: `0 0 0 6px #0A0E17, 0 0 8px 1px ${phase.color}`, transform: 'translateY(-50%) scale(1.1)' },
                  '&:hover .timeline-card': { transform: 'scale(1.03)', borderColor: phase.color, boxShadow: `0 0 8px -2px ${phase.color}` },
                  '&:hover .timeline-card .timeline-title': { color: phase.color },
                  '&:hover .timeline-card::after': { opacity: 1, animation: 'cardShine 1s ease-in-out forwards' },
                }}>
                  {/* Dot */}
                  <Box className="timeline-dot" sx={{
                    position: 'absolute', left: { xs: '-52px', md: '-68px' }, top: '50%', transform: 'translateY(-50%)',
                    width: 32, height: 32, borderRadius: '50%',
                    border: `2px solid ${phase.color}`, bgcolor: '#0A0E17',
                    fontFamily: '"Space Grotesk", sans-serif',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.75rem', fontWeight: 700, color: phase.color,
                    zIndex: 3, boxShadow: '0 0 0 6px #0A0E17',
                    transition: 'all 0.3s ease',
                  }}>
                    {phase.num}
                  </Box>
                  {/* Card */}
                  <Box className="timeline-card" sx={{
                    position: 'relative', overflow: 'hidden',
                    bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)',
                    borderRadius: '14px', p: { xs: '16px 20px', md: '20px 24px' },
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    '&::after': {
                      content: '""', position: 'absolute', inset: 0, borderRadius: '14px', pointerEvents: 'none', opacity: 0,
                      background: 'linear-gradient(105deg, transparent 0%, transparent 40%, rgba(255,255,255,0.03) 42%, rgba(255,255,255,0.08) 45%, rgba(255,255,255,0.12) 47%, rgba(255,255,255,0.08) 49%, rgba(255,255,255,0.03) 52%, transparent 54%, transparent 100%)',
                      backgroundSize: '250% 100%', backgroundPosition: '200% center',
                      transition: 'opacity 0.3s ease',
                    },
                  }}>
                    <Typography className="timeline-title" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.1rem', mb: 0.5, transition: 'color 0.3s ease' }}>
                      {phase.title}
                    </Typography>
                    <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: 1.7 }}>{phase.desc}</Typography>
                  </Box>
                </Box>
              </FadeSection>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ══════════════════ PRICING ══════════════════ */}
      <Box id="pricing" sx={{ py: { xs: 10, md: 14 }, position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <GlowOrb top="40%" left="50%" size={600} color="rgba(0,255,148,0.04)" />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Chip label="PRICING" sx={{ mb: 2, bgcolor: 'rgba(0,255,148,0.1)', color: '#00FF94', border: '1px solid rgba(0,255,148,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Simple, Transparent{' '}
                <Box component="span" sx={{ ...shinyText('#00FF94') }}>Pricing</Box>
              </Typography>
              <Typography sx={{ color: '#9CA3AF', maxWidth: 550, mx: 'auto', fontSize: '1.05rem' }}>
                Start free. Upgrade when you're ready. No hidden fees.
              </Typography>
            </Box>
            {/* Billing Period Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 8 }}>
              <Box sx={{ display: 'inline-flex', bgcolor: 'rgba(17,24,39,0.8)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3, p: 0.5 }}>
                {billingOptions.map((opt) => (
                  <Button key={opt.key} onClick={() => setBillingPeriod(opt.key)}
                    sx={{
                      px: { xs: 2, sm: 3 }, py: 1, borderRadius: 2.5, fontSize: '0.9rem', fontWeight: 600,
                      color: billingPeriod === opt.key ? '#0A0E17' : '#9CA3AF',
                      bgcolor: billingPeriod === opt.key ? '#00FF94' : 'transparent',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: billingPeriod === opt.key ? '#33FFaa' : 'rgba(255,255,255,0.05)' },
                    }}>
                    {opt.label}
                    {opt.discount && billingPeriod === opt.key && (
                      <Chip label={opt.discount} size="small" sx={{ ml: 1, height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(10,14,23,0.3)', color: '#0A0E17' }} />
                    )}
                    {opt.discount && billingPeriod !== opt.key && (
                      <Chip label={opt.discount} size="small" sx={{ ml: 1, height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(0,255,148,0.12)', color: '#00FF94' }} />
                    )}
                  </Button>
                ))}
              </Box>
            </Box>
          </FadeSection>
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {plans.map((p, i) => {
              const currentPrice = p.prices[billingPeriod];
              const monthlyPrice = p.prices.monthly;
              const billedTotal = billingPeriod === 'yearly' ? currentPrice * 12 : billingPeriod === '6month' ? currentPrice * 6 : null;
              const billedLabel = billingPeriod === 'yearly' ? 'Billed annually' : billingPeriod === '6month' ? 'Billed every 6 months' : null;
              return (
              <Grid item xs={12} md={4} key={i} sx={{ display: 'flex' }}>
                <FadeSection delay={i * 0.1} sx={{ display: 'flex', width: '100%' }}>
                  <GlassCard hover={false} sx={{
                    height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'visible',
                    ...(p.highlighted && { border: '1px solid rgba(0,255,148,0.3)', boxShadow: '0 0 40px rgba(0,255,148,0.08)' }),
                  }}>
                    {p.badge && (
                      <Chip label={p.badge} sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', bgcolor: '#00FF94', color: '#0A0E17', fontWeight: 700, fontSize: '0.8rem' }} />
                    )}
                    <CardContent sx={{ p: 4, flexGrow: 1 }}>
                      <Typography variant="h5" sx={{ mb: 1 }}>{p.name}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '3rem', fontWeight: 800, color: p.highlighted ? '#00FF94' : '#F9FAFB', transition: 'all 0.3s ease' }}>${currentPrice}</Typography>
                        <Typography sx={{ color: '#9CA3AF' }}>/mo</Typography>
                        {billingPeriod !== 'monthly' && (
                          <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', textDecoration: 'line-through', ml: 0.5 }}>${monthlyPrice}</Typography>
                        )}
                      </Box>
                      {billedTotal && (
                        <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mb: 2 }}>
                          {billedLabel} &mdash; ${billedTotal} total
                        </Typography>
                      )}
                      {!billedTotal && <Box sx={{ mb: 2 }} />}
                      <Typography sx={{ color: '#9CA3AF', mb: 3, fontSize: '0.9rem' }}>{p.desc}</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {p.features.map((f, fi) => (
                          <Box key={fi} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <CheckCircle sx={{ fontSize: 18, color: '#00FF94' }} />
                            <Typography sx={{ fontSize: '0.9rem', color: '#D1D5DB' }}>{f}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ p: 4, pt: 0 }}>
                      <Button fullWidth variant={p.highlighted ? 'contained' : 'outlined'} size="large" component={RouterLink} to="/register"
                        sx={p.highlighted ? { py: 1.5 } : { py: 1.5, borderColor: 'rgba(255,255,255,0.15)', color: '#F9FAFB', '&:hover': { borderColor: '#00FF94', color: '#00FF94' } }}>
                        {p.cta}
                      </Button>
                    </CardActions>
                  </GlassCard>
                </FadeSection>
              </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════ FAQ ══════════════════ */}
      <Box id="faq" sx={{ py: { xs: 10, md: 14 }, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <Container maxWidth="md">
          <FadeSection>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Chip label="FAQ" sx={{ mb: 2, bgcolor: 'rgba(0,255,148,0.1)', color: '#00FF94', border: '1px solid rgba(0,255,148,0.2)', fontWeight: 700, letterSpacing: '0.1em' }} />
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
                Frequently Asked Questions
              </Typography>
            </Box>
          </FadeSection>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {faqs.map((f, i) => (
              <FadeSection key={i} delay={i * 0.06}>
                <Accordion elevation={0} sx={{ bgcolor: 'rgba(17,24,39,0.6)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px !important', '&:before': { display: 'none' }, '&.Mui-expanded': { border: '1px solid rgba(0,255,148,0.15)' } }}>
                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#9CA3AF' }} />} sx={{ px: 3, py: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>{f.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Typography sx={{ color: '#9CA3AF', lineHeight: 1.8, fontSize: '0.95rem' }}>{f.a}</Typography>
                  </AccordionDetails>
                </Accordion>
              </FadeSection>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ══════════════════ FINAL CTA ══════════════════ */}
      <Box sx={{ py: { xs: 10, md: 16 }, position: 'relative', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <GlowOrb top="30%" left="50%" size={700} color="rgba(0,255,148,0.06)" />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <FadeSection>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '3rem' }, mb: 3 }}>
                Ready to Trade with{' '}
                <Box component="span" sx={{ ...shinyGradient('linear-gradient(135deg, #00FF94, #3B82F6)') }}>
                  AI Precision?
                </Box>
              </Typography>
              <Typography sx={{ color: '#9CA3AF', fontSize: '1.1rem', mb: 5, maxWidth: 500, mx: 'auto', lineHeight: 1.7 }}>
                Build, deploy, and optimize autonomous trading strategies with aitrader.Gold.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button variant="contained" size="large" component={RouterLink} to="/register" endIcon={<ArrowForward />} sx={{ px: 5, py: 1.6, fontSize: '1.1rem' }}>
                  Start Free Trial
                </Button>
                <Button variant="outlined" size="large" component={RouterLink} to="/login" sx={{ px: 5, py: 1.6, fontSize: '1.1rem', borderColor: 'rgba(255,255,255,0.15)', color: '#F9FAFB', '&:hover': { borderColor: '#00FF94', color: '#00FF94' } }}>
                  Login
                </Button>
              </Box>
              <Typography sx={{ color: '#6B7280', mt: 3, fontSize: '0.85rem' }}>
                No credit card required &bull; 14-day free trial &bull; Cancel anytime
              </Typography>
            </Box>
          </FadeSection>
        </Container>
      </Box>

      {/* ══════════════════ FOOTER ══════════════════ */}
      <Box component="footer" sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ShowChart sx={{ color: '#00FF94', fontSize: 28 }} />
                <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#00FF94' }}>NexusTrader</Typography>
              </Box>
              <Typography sx={{ color: '#6B7280', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: 300 }}>
                Next-generation AI trading platform. Build autonomous strategies with institutional-grade risk management.
              </Typography>
            </Grid>
            {[
              { title: 'Platform', items: [
                { label: 'Features', href: '#features', scroll: true },
                { label: 'Pricing', href: '#pricing', scroll: true },
                { label: 'Documentation', href: '/docs' },
                { label: 'API Docs', href: '/docs#templates' },
              ]},
              { title: 'Company', items: [
                { label: 'About', href: '/about' },
                { label: 'Blog', href: '/blog' },
                { label: 'Careers', href: '/about' },
                { label: 'Contact', href: '/contact' },
              ]},
              { title: 'Legal', items: [
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Risk Disclosure', href: '/risk-disclosure' },
                { label: 'Cookie Policy', href: '/cookies' },
              ]},
            ].map((col, ci) => (
              <Grid item xs={6} md={2} key={ci}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2 }}>{col.title}</Typography>
                {col.items.map(item => (
                  <Typography
                    key={item.label}
                    component="a"
                    href={item.href}
                    onClick={item.scroll ? (e) => { e.preventDefault(); document.getElementById(item.href.slice(1))?.scrollIntoView({ behavior: 'smooth' }); } : undefined}
                    sx={{ display: 'block', color: '#6B7280', fontSize: '0.9rem', py: 0.5, cursor: 'pointer', transition: 'color 0.2s', textDecoration: 'none', '&:hover': { color: '#00FF94' } }}
                  >{item.label}</Typography>
                ))}
              </Grid>
            ))}
          </Grid>
          <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
            <Typography sx={{ color: '#4B5563', fontSize: '0.85rem' }}>&copy; {new Date().getFullYear()} aitrader.Gold. All rights reserved.</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {[
                { label: 'Privacy', href: '/privacy' },
                { label: 'Risk Disclosure', href: '/risk-disclosure' },
                { label: 'Cookies', href: '/cookies' },
              ].map(l => (
                <Typography key={l.label} component="a" href={l.href} sx={{ color: '#4B5563', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'none', '&:hover': { color: '#9CA3AF' } }}>{l.label}</Typography>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

    </Box>
    </ThemeProvider>
  );
};

export default LandingPage;
